import type { JSX, ReactNode } from "react";

import { t } from "@/i18n";

import { Illustration, type IllustrationName } from "./brand";
import { Button } from "./button";

interface StateAction {
  label: string;
  onClick: () => void;
}

/** The whole page's state, left-aligned under its illustration (the K3 shell). */
function PageState({ name, title, role, children }: { name: IllustrationName; title: string; role?: "alert" | undefined; children: ReactNode }): JSX.Element {
  return (
    <div role={role} className="mx-auto my-24 flex max-w-115 flex-col items-start gap-2.5 px-5 max-sm:my-12">
      <span className="mb-1.5">
        <Illustration name={name} />
      </span>
      <b className="text-h2 font-semibold">{title}</b>
      {children}
    </div>
  );
}

/** A panel's or a list's state, centred inside it (`Komponentlar`). */
function InlineState({ name, role, children }: { name: IllustrationName; role?: "alert" | undefined; children: ReactNode }): JSX.Element {
  return (
    <div role={role} className="flex flex-col items-center gap-2 px-6 py-8 text-center">
      <Illustration name={name} width={96} />
      {children}
    </div>
  );
}

/**
 * Empty: an illustration, the fact and one action. `page` is the full-screen form with a `title`;
 * without it the block fits a list or a panel. A list empty because of a filter passes `none` and
 * a reset as its action, not a create button.
 */
export function EmptyState({
  title,
  message,
  action,
  illustration = "none",
  page = false,
}: {
  title?: string | undefined;
  message: string;
  action?: StateAction | undefined;
  illustration?: IllustrationName | undefined;
  page?: boolean | undefined;
}): JSX.Element {
  if (page) {
    return (
      <PageState name={illustration} title={title ?? message}>
        {title === undefined ? null : <p className="text-base text-ink-muted">{message}</p>}
        {action === undefined ? null : (
          <Button className="mt-1.5" onClick={action.onClick}>
            {action.label}
          </Button>
        )}
      </PageState>
    );
  }
  return (
    <InlineState name={illustration}>
      {title === undefined ? null : <b className="text-lg font-semibold">{title}</b>}
      <p className="text-sm text-ink-muted">{message}</p>
      {action === undefined ? null : (
        <Button variant="secondary" className="mt-1.5" onClick={action.onClick}>
          {action.label}
        </Button>
      )}
    </InlineState>
  );
}

/** A failed request: what failed and "Qayta urinish". Never a stack trace. */
export function ErrorState({
  title,
  message,
  retryLabel = t("ui.retry"),
  onRetry,
  page = false,
  code,
  secondary,
}: {
  title?: string | undefined;
  message: string;
  retryLabel?: string | undefined;
  onRetry?: (() => void) | undefined;
  page?: boolean | undefined;
  /** "Xato 503 · 4 noy, 10:42": what to quote to support. Page form only. */
  code?: string | undefined;
  /** A second way forward beside the retry, such as a link. Page form only. */
  secondary?: ReactNode | undefined;
}): JSX.Element {
  if (page) {
    return (
      <PageState name="err" title={title ?? t("ui.error.title")} role="alert">
        <p className="text-base text-ink-muted">{message}</p>
        {code === undefined ? null : <span className="font-mono text-xs text-ink-subtle">{code}</span>}
        <div className="mt-1.5 flex flex-wrap gap-2">
          {onRetry === undefined ? null : <Button onClick={onRetry}>{retryLabel}</Button>}
          {secondary}
        </div>
      </PageState>
    );
  }
  return (
    <InlineState name="err" role="alert">
      {title === undefined ? null : <b className="text-lg font-semibold">{title}</b>}
      <p className="text-sm text-ink">{message}</p>
      {onRetry === undefined ? null : (
        <Button variant="secondary" className="mt-1.5" onClick={onRetry}>
          {retryLabel}
        </Button>
      )}
    </InlineState>
  );
}

/** The role may not see this: stated plainly, with a way back when the caller gives one. */
export function NoPermission({ message, home, page = false }: { message: string; home?: StateAction | undefined; page?: boolean | undefined }): JSX.Element {
  if (page) {
    return (
      <PageState name="deny" title={t("ui.denied.title")}>
        <p className="text-base text-ink-muted">{message}</p>
        {home === undefined ? null : (
          <Button variant="secondary" className="mt-1.5" onClick={home.onClick}>
            {home.label}
          </Button>
        )}
      </PageState>
    );
  }
  return (
    <InlineState name="deny">
      <b className="text-lg font-semibold">{t("ui.denied.title")}</b>
      <p className="text-sm text-ink-muted">{message}</p>
    </InlineState>
  );
}

/** A labelled group of state specimens, for the ui-kit page. */
export function StateSpecimen({ label, children }: { label: string; children: ReactNode }): JSX.Element {
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <span className="text-xs font-medium text-ink-subtle">{label}</span>
      <div className="rounded-card border border-line bg-surface">{children}</div>
    </div>
  );
}
